(() => {
  if (!('customElements' in window) || customElements.get('logo-marquee')) return;

  class LogoMarquee extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.dataset.logoLayout !== 'scrolling') return;

      this.viewport = this.querySelector('[data-logo-viewport]');
      this.track = this.querySelector('[data-logo-track]');
      this.group = this.querySelector('[data-logo-group]');
      if (!this.viewport || !this.track || !this.group) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.hoverPaused = false;
      this.focusPaused = false;
      this.pointerPaused = false;
      this.editorPaused = false;
      this.pagePaused = false;
      this.heldPhase = null;
      this.inView = true;
      this.loopDistance = 0;
      this.phase = 0;
      this.motionAnimation = null;
      this.animationDirection = this.dataset.scrollDirection;
      this.rebuildRaf = 0;
      this.lastViewportWidth = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverCapability = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.bindEvents();
      this.resizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width ?? this.viewport.clientWidth;
        if (Math.abs(width - this.lastViewportWidth) < 0.5) return;
        this.lastViewportWidth = width;
        this.scheduleRebuild();
      });
      this.resizeObserver.observe(this.viewport);
      this.mutationObserver = new MutationObserver(() => this.scheduleRebuild());
      this.mutationObserver.observe(this.group, { childList: true });
      this.mutationObserver.observe(this, {
        attributes: true,
        attributeFilter: ['data-auto-scroll', 'data-scroll-direction', 'data-scroll-speed']
      });
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0]?.isIntersecting ?? true;
        this.syncPlayback();
      });
      this.intersectionObserver.observe(this);
      this.scheduleRebuild();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (!this.initialized) return;
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.motionAnimation?.cancel();
      this.motionAnimation = null;
      window.cancelAnimationFrame(this.rebuildRaf);
      window.clearTimeout(this.pointerTimer);
      window.clearTimeout(this.focusTimer);
      this.track?.style.removeProperty('transform');
      this.initialized = false;
    }

    bindEvents() {
      const options = { signal: this.abortController.signal };

      this.addEventListener('pointerenter', () => {
        if (!this.hoverCapability.matches) return;
        this.hoverPaused = true;
        this.syncPlayback();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hoverPaused = false;
        this.syncPlayback();
      }, options);
      this.hoverCapability.addEventListener('change', () => {
        if (!this.hoverCapability.matches) this.hoverPaused = false;
        this.syncPlayback();
      }, options);
      this.addEventListener('focusin', event => {
        this.focusPaused = true;
        if (event.target.matches('.logo-list--logo')) {
          event.target.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
        this.syncPlayback();
      }, options);
      this.addEventListener('focusout', () => {
        window.clearTimeout(this.focusTimer);
        this.focusTimer = window.setTimeout(() => {
          const activeElement = document.activeElement;
          this.focusPaused = this.contains(activeElement);
          this.syncPlayback();
        });
      }, options);
      this.viewport.addEventListener('pointerdown', () => {
        window.clearTimeout(this.pointerTimer);
        this.pointerPaused = true;
        this.syncPlayback();
      }, options);
      window.addEventListener('pointerup', () => this.releasePointerPause(), options);
      window.addEventListener('pointercancel', () => this.releasePointerPause(), options);
      this.viewport.addEventListener('wheel', () => {
        this.pointerPaused = true;
        this.syncPlayback();
        this.releasePointerPause();
      }, { ...options, passive: true });
      this.reducedMotion.addEventListener('change', () => this.scheduleRebuild(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.holdPhase();
        } else {
          this.restoreHeldPhase();
        }
        this.syncPlayback();
      }, options);
      window.addEventListener('pagehide', () => {
        this.pagePaused = true;
        this.holdPhase();
        this.syncPlayback();
      }, options);
      window.addEventListener('pageshow', () => {
        this.pagePaused = false;
        this.restoreHeldPhase();
        this.syncPlayback();
      }, options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target === this || event.target.contains(this)) this.destroy();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        this.editorPaused = true;
        this.syncPlayback();
        event.target.scrollIntoView({ block: 'nearest', inline: 'center' });
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.contains(event.target)) return;
        this.editorPaused = false;
        this.syncPlayback();
      }, options);
    }

    releasePointerPause() {
      window.clearTimeout(this.pointerTimer);
      this.pointerTimer = window.setTimeout(() => {
        this.pointerPaused = false;
        this.syncPlayback();
      }, 1200);
    }

    scheduleRebuild() {
      window.cancelAnimationFrame(this.rebuildRaf);
      this.rebuildRaf = window.requestAnimationFrame(() => {
        this.rebuildRaf = 0;
        this.rebuild();
      });
    }

    currentPhase() {
      if (!this.motionAnimation || !this.motionAnimation.effect) return this.phase;
      const timing = this.motionAnimation.effect.getComputedTiming();
      const progress = timing.progress ?? 0;
      return this.animationDirection === 'right'
        ? (1 - progress) % 1
        : progress;
    }

    setAnimationPhase(phase) {
      if (!this.motionAnimation?.effect) return;
      const duration = this.motionAnimation.effect.getComputedTiming().duration;
      const animationProgress = this.animationDirection === 'right'
        ? (1 - phase) % 1
        : phase;
      this.motionAnimation.currentTime = animationProgress * duration;
    }

    holdPhase() {
      if (!this.motionAnimation) return;
      this.heldPhase = this.currentPhase();
      this.motionAnimation.pause();
      this.setAnimationPhase(this.heldPhase);
    }

    restoreHeldPhase() {
      if (this.heldPhase === null) return;
      this.phase = this.heldPhase;
      this.setAnimationPhase(this.heldPhase);
      this.heldPhase = null;
    }

    rebuild() {
      if (!this.initialized) return;

      this.phase = this.currentPhase();
      this.motionAnimation?.cancel();
      this.motionAnimation = null;
      this.track.style.removeProperty('transform');
      this.track.querySelectorAll('[data-logo-clone]').forEach(clone => clone.remove());

      const itemCount = this.group.querySelectorAll('.logo-list--logo').length;
      const trackStyle = getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      this.loopDistance = this.group.getBoundingClientRect().width + gap;

      if (itemCount < 2 || this.loopDistance <= 0) {
        this.loopDistance = 0;
        this.phase = 0;
        return;
      }

      const cloneCount = Math.max(1, Math.ceil(this.viewport.clientWidth / this.loopDistance) + 1);
      for (let index = 0; index < cloneCount; index += 1) {
        const clone = this.group.cloneNode(true);
        clone.removeAttribute('data-logo-group');
        clone.dataset.logoClone = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach(element => element.removeAttribute('data-shopify-editor-block'));
        clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(element => element.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
      }

      if (this.dataset.autoScroll === 'true' && !this.reducedMotion.matches) {
        const speed = Math.max(1, Number(this.dataset.scrollSpeed) || 30);
        const duration = this.loopDistance / speed * 1000;
        this.animationDirection = this.dataset.scrollDirection;
        const keyframes = this.animationDirection === 'right'
          ? [
              { transform: `translate3d(${-this.loopDistance}px, 0, 0)` },
              { transform: 'translate3d(0, 0, 0)' }
            ]
          : [
              { transform: 'translate3d(0, 0, 0)' },
              { transform: `translate3d(${-this.loopDistance}px, 0, 0)` }
            ];

        this.motionAnimation = this.track.animate(keyframes, {
          duration,
          iterations: Infinity,
          easing: 'linear',
          fill: 'both'
        });
        this.motionAnimation.pause();
        this.setAnimationPhase(this.phase);
      } else {
        this.phase = 0;
      }

      this.syncPlayback();
    }

    shouldPlay() {
      return Boolean(this.motionAnimation)
        && !this.hoverPaused
        && !this.focusPaused
        && !this.pointerPaused
        && !this.editorPaused
        && !this.pagePaused
        && !this.reducedMotion.matches
        && this.inView
        && !document.hidden;
    }

    syncPlayback() {
      if (!this.motionAnimation) return;
      if (this.shouldPlay()) {
        if (this.motionAnimation.playState !== 'running') this.motionAnimation.play();
      } else if (this.motionAnimation.playState === 'running') {
        this.motionAnimation.pause();
      }
    }
  }

  customElements.define('logo-marquee', LogoMarquee);
})();
